import { IBadge } from '../screens/Profile/screens/Leaderboard/types';
import { IMango } from './feedTypes';
import { ITQShoutout } from './tqConnectTypes';

export interface IChannelGroup {
  [x: string]: any;
  _id: string;
  host: string;
  creator: string;
  name: string;
  hideFromNonChannelMember: boolean;
  rank: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  channelArr: IChannel[];
}

export enum CommunityChannelTabs {
  FEED = 'feed',
  MESSAGES = 'messages',
}

export type ICommunityChannelTabTypes = `${CommunityChannelTabs}`;

interface GroupArr {
  _id: string;
  name: string;
  rank: number;
}

interface BadgeArr {
  _id: string;
  name: string;
}

interface MangoArr {
  _id: string;
  title: string;
}

export interface IChannelDetails {
  _id: string;
  name: string;
  description: string;
  creator: string;
  visibility: string;
  features: ICommunityChannelTabTypes[];
  archived: boolean;
  communityProfilePicture: string;
  communityProfilePictureType: string;
  createdAt: string;
  featureSettings: IFeatureSettings;
  updatedAt: string;
  chatRoom: string;
  mangoArr: MangoArr[];
  badgeArr: BadgeArr[];
  groupArr: GroupArr[];
}

export interface IChannel {
  _id: string;
  name: string;
  description: string;
  creator: string;
  mangoArr: string[];
  badgeArr: string[];
  groupArr: string;
  visibility: string;
  features: ICommunityChannelTabTypes[];
  archived: boolean;
  communityProfilePicture: string;
  communityProfilePictureType: string;
  createdAt: string;
  featureSettings: IFeatureSettings;
  updatedAt: string;
  chatRoom?: string;
  isLocked?: boolean;
  feedMuted?: boolean;
  messageNotificationsMuted?: boolean;
}

export interface ICommentToEdit extends IComment {
  isReplay?: boolean;
  parentId?: string;
}

export interface IComment {
  _id: string;
  creator: string;
  channel: string;
  post: string;
  mentions: string[];
  repliedComment: string;
  comment: string;
  images: string[];
  totalLikes: number;
  totalReplies: number;
  updatedAt: string;
  createdAt: string;
  commentedBy: ICommentedBy;
  isLiked: boolean;
  parentCommentId?: string;
  viewReply?: boolean;
  replyTo?: ICommentedBy;
  isEdited?: boolean;
}

export interface ICommentedBy {
  _id: string;
  name: string;
  profilePicUrl: string;
  isBlockedFromCommunityEngagement: boolean;
}

export interface ILink {
  _id: string;
  host: string;
  creator: string;
  link: string;
  title: string;
  rank: number;
}

interface IFeatureSettings {
  feed: IFeedSettings;
  messages: IMessagesSettings;
}

interface ISharingControlSettings {
  allowAudioSharing: boolean;
  allowPhotoSharing: boolean;
  allowFileSharing: boolean;
  allowPollSharing: boolean;
  allowVideoSharing: boolean;
}

interface IMessagesSettings {
  allowEveryoneToMessage: boolean;
  allowPeerToPeerMessaging: boolean;
  sharingControlSettings?: ISharingControlSettings;
}

interface IFeedSettings {
  allowMemberPosts: boolean;
}

export interface IPostState {
  loading: boolean;
  page: number;
  hasMore: boolean;
  posts: IChannelPostDetails[];
  refreshing: boolean;
}

export interface IPostProgressData {
  post?: any;
  message: string;
  loaded: number;
  total: number;
  showProgress: boolean;
}

export enum SortBy {
  Latest = 'latest',
  Oldest = 'oldest',
  TrendingNow = 'trending_now',
  MostPopular = 'most_popular',
}

export type ISortBy = `${SortBy}`;

export enum Showing {
  MyPosts = 'my_posts',
  PostsFromSubscribers = 'posts_from_subscribers',
  Everything = 'everything',
  PostsFromCreator = 'posts_from_creator',
}

export type IShowing = `${Showing}`;

export interface IUserCommunityAccess {
  communityCreator: string;
  hasCommunityCreatorAccess: boolean;
  canPostInMangoes: boolean;
  canPostInChannels: boolean;
  accessChannelsToPost: IChannel[];
  accessChannels: IChannel[];
  accessMangoes: IMango[];
  accessBadges: IBadge[];
  hasTQConnectAccessForUser: boolean;
}

export interface IMetadata {
  feedUnreadCount: number;
  messageUnreadCount: number;
}

export interface ICommunitySlice {
  userCommunityAccess: IUserCommunityAccess;
  filteredMangoes: any[];
  showing: { title: string; _id: IShowing }[];
  sortBy: { title: string; _id: ISortBy }[];
  activeSection: string;
  headerTitle: string;
  headerIcon: Element | null;
  groupChannelList: IChannelGroup[];
  channelPageActiveTab: ICommunityChannelTabTypes;
  selectedChannelData?: IChannel | null;
  communityLinkList: ILink[];
  communityPostState: IPostState;
  lastPostId: string;
  postProgressData: IPostProgressData;
  mediaProgressData: IPostProgressData;
  communityViewedPostIds: string[];
  pdpCommunityPostData: null | IChannelPostDetails;
  communityPostComments: IComment[];
  replyComment: IComment | null;
  replayCommentsObj: { [key: string]: IComment[] };
  globalCarouselMediaList: IPostMediaItem[];
  likedByUsersModalState: {
    visible: boolean;
    postId: string;
    channelId: string;
  };
  metadata: Record<string, IMetadata>;
  commentToEdit: ICommentToEdit | null;
}

export interface IPollOption {
  value: string;
  _id: string;
}

export interface IChannelPostDetails {
  _id: string;
  postedBy: string;
  channel: string;
  isPinned: boolean;
  totalViews: number;
  totalLikes: number;
  totalComments: number;
  totalParentComments: number;
  totalRepost: number;
  scheduleAt: string;
  updatedAt: string;
  createdAt: string;
  post: IChannelPost;
  isLiked: boolean;
  totalPollVotes?: number;
  pollVotesPerOption?: { [key: string]: number };
  votedOption?: string | false;
}

export type IPostDetailsWithoutPost = Pick<
  IChannelPostDetails,
  | '_id'
  | 'postedBy'
  | 'channel'
  | 'isPinned'
  | 'totalViews'
  | 'totalLikes'
  | 'totalComments'
  | 'totalParentComments'
  | 'totalRepost'
  | 'scheduleAt'
  | 'updatedAt'
  | 'createdAt'
  | 'isLiked'
>;

export interface IPostPollOption {
  optionContent: string;
  optionNumber: number;
  optionId: string;
}

export interface IPostedBy {
  _id: string;
  name: string;
  email: string;
  phone: number;
  profilePicUrl: string;
}

interface INotificationSettings {
  isLiveNotificationEnabled: boolean;
  isWhatsappNotificationEnabled: boolean;
  isEmailNotificationEnabled: boolean;
}

export interface IPostSettings {
  disableComment: boolean;
  hideComment: boolean;
  hideLikes: boolean;
}

export interface IPostMediaContent {
  _id: string;
  contentType: string;
  contentUrl: string;
  fileName?: string;
  fileSize?: number;
  thumbnail?: string;
  compressedVideoUrl?: string;
  compressedVideoUrlDash?: string;
  compressedVideoUrlHls?: string;
  isCompressDone?: boolean;
  isCompressError?: boolean;
  compressErrorDescription?: string;
  compressErrorDescriptionWarnings?: string;
  transcodingJob?: string;
}

export interface IPostMediaItem extends ISelFile, Partial<IPostMediaContent> {
  postedBy?: string;
  postId?: string;
}

export interface IChannelPoll {
  _id: string;
  creator: string;
  question: string;
  pollOptions: IPostPollOption[];
  pollStartAt: string;
  pollEndAt: string;
  isResultVisibleToMembers: boolean;
  allowVoteChange: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IChannelPost {
  _id: string;
  creator: string;
  caption: string;
  layoutType?: IPostMediaLayout;
  mediaContent: IPostMediaContent[];
  postSettings: IPostSettings;
  notificationSettings: INotificationSettings;
  scheduleAt: string;
  updatedAt: string;
  createdAt: string;
  channels: IChannel[];
  postedBy: IPostedBy;
  poll?: IChannelPoll;
  postRef?: IChannelPost;
  tqConnectShoutout?: ITQShoutout;
}

export interface IPostReqBody {
  channels: string[];
  caption: string;
  postRef?: string;
  layoutType?: IPostMediaLayout;
  mediaContent?: IPostReqBodyMediaContent[];
  postSettings?: IPostReqBodyPostSettings;
  pollOptions?: IPostReqBodyPollOption[];
  pollSettings?: IPostReqBodyPollSettings;
  scheduleAt?: string;
  tqConnectShoutout?: string;
}

export type IPostMediaLayout = 'carousel' | 'collage';

interface IPostReqBodyPollSettings {
  isResultVisibleToMembers: boolean;
  allowVoteChange: boolean;
  startDate: string;
  endDate: string;
}

interface IPostReqBodyPollOption {
  optionContent: string;
  optionNumber: number;
}

export interface IPostReqBodyPostSettings {
  disableComment: boolean;
  hideComment: boolean;
  hideLikes: boolean;
}

export interface IPostReqBodyMediaContent {
  type: string;
  contentUrl: string;
  _id: string;
}

export type TFileTypes = 'image' | 'audio' | 'video' | 'doc' | 'file';

export interface ISelFile {
  file?: File;
  name?: string;
  fileName?: string;
  fileSize?: number;
  ext?: string;
  type: TFileTypes;
  url: string;
}

export interface ICommunityUploadedMedia {
  type: string;
  filename: string;
  path: string;
  assetUrl: string;
  _id: string;
}

export interface IFileUploadAsset {
  assetId: string;
  url: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  contentType: string;
}
export interface IFileUploadResponse {
  assets: IFileUploadAsset[];
}

export interface IEditPostReqBody {
  channels: string[];
  caption: string;
  postSettings: IPostSettings;
}

export interface ICommentReqBody {
  repliedComment?: string;
  comment: string;
  mentions?: string;
  images?: string[];
}

export interface IGlobalCarouselMedia {
  type: string;
  url: string;
}

export interface ILikedByUser {
  isFan: boolean;
  name: string;
  profilePicUrl: string;
  userId: string;
  _id: string;
}
